import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSession } from '@hooks/security/use-session';
import { useNkrCenters } from '@hooks/register/use-nkr-centers';
import { useLocalize } from '@hooks/use-localize';
import { useCenterFilterHeight } from '@/hooks/use-header-height';
import type { INkrCenterOption, INkrCentersModel, INkrCentersSubmitResult } from '@/types/history/nkr-centers';
import CenterTable from '@views/register/nkr-centers-sections/center-table';
import CenterSelectModals from '@views/register/nkr-centers-sections/center-select-modals';

const DISTANCE_OPTIONS = [
    { value: 300, key: 'DashLabel_300Miles', fallback: '300 miles' },
    { value: 1000, key: 'DashLabel_1000Miles', fallback: '1,000 miles' },
    { value: 0, key: 'DashLabel_AnyDistance', fallback: 'Any distance' },
];

const formatDate = (value: string | null) =>
    value ? new Date(value).toLocaleDateString('en-US') : '';

const NkrCenters = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, isRefreshing, error, changeFilter, submit, isSubmitting, submitError } = useNkrCenters(registrationId, editKey);

    if (isLoading) {
        return (
            <div className="row">
                <div className="col-xs-12">Loading&hellip;</div>
            </div>
        );
    }

    return (
        <>
            {error && (
                <div className="row">
                    <div className="col-xs-12">
                        <div className="alert alert-danger">{error}</div>
                    </div>
                </div>
            )}

            <NkrCentersContent
                model={model}
                isRefreshing={isRefreshing}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onChangeFilter={changeFilter}
                onSubmit={submit}
            />
        </>
    );
};

interface INkrCentersContentProps {
    model: INkrCentersModel;
    isRefreshing: boolean;
    isSubmitting: boolean;
    submitError: string | null;
    onChangeFilter: (distance: number, showAdvanceReimbursementCentersOnly: boolean) => void;
    onSubmit: (centerId: number) => Promise<INkrCentersSubmitResult>;
}

const NkrCentersContent = ({
    model,
    isRefreshing,
    isSubmitting,
    submitError,
    onChangeFilter,
    onSubmit,
}: INkrCentersContentProps) => {
    const t = useLocalize(model.languageData);
    const navigate = useNavigate();

    const centerFilterRef = useRef<HTMLDivElement>(null);

    useCenterFilterHeight(centerFilterRef, true);

    const [pendingDistance, setPendingDistance] = useState(model.distance);
    const [pendingShowAdvanceReimbursementOnly, setPendingShowdvanceReimbursementOnly] = useState(model.showAdvanceReimbursementOnly)
    const [selectedCenter, setSelectedCenter] = useState<INkrCenterOption | null>(null);

    const dataAsOf = formatDate(model.dataAsOfDate);

    const totalTransplantsPopover =
        t('DashPopover_TotalTransplants', 'The total number of living donor kidney transplants that have taken place at this center.') +
        '\n* Data as of ' +
        dataAsOf;

    const pastYearFallback =
        'The number of living donor kidney transplants that took place at this center between ' +
        formatDate(model.pastYearStartDate) +
        ' and ' +
        dataAsOf +
        '.';

    const pastYearPopover =
        t('DashPopover_PastYearTransplants', pastYearFallback) + '\n* Data as of ' + dataAsOf;

    const onConfirmSelect = async () => {
        if (!selectedCenter) return;

        const result = await onSubmit(selectedCenter.centerId);

        if (result.ok) {
            navigate('/register/donate');
        }
    };

    const tableProps = {
        t,
        registrationId: model.id,
        editKey: model.editKey,
        isFemale: model.isFemale,
        isLiver: model.isLiver,
        totalTransplantsPopover,
        pastYearPopover,
        isSubmitting,
        onSelect: setSelectedCenter,
    };

    const hasCenters =
        model.universalDashOption != null ||
        model.patientCenterOption != null ||
        model.dcnOptions.length > 0 ||
        model.nutcrackerOptions.length > 0 ||
        model.otherOptions.length > 0;

    return (
        <>
            <style>{`
                @media (min-width: 1200px) {
                    .center-select-container {
                        width: 1170px;
                    }
                }

                .center-select-container .row {
                    margin-left: 0px;
                    margin-right: 0px;
                }

                .center-select-container [class^="col-xs"] {
                    padding-left: 0px;
                    padding-right: 0px;
                }
            `}</style>
            <div className='container center-select-container'>
                {model.isIE && (
                    <div className="row">
                        <div className="col-xs-12">
                            <div className="alert alert-warning">
                                {t('Alert_BrowserRecommend', 'To ensure proper function of this site, we recommend that you use Chrome or FireFox.')}
                                .
                            </div>
                        </div>
                    </div>

                )}

                <div className='row'>
                    <div className='col-xs-12 col-sm-12'>
                        <div className="row">
                            <div className="col-xs-12">
                                <h1 style={{ marginTop: 0 }}>
                                    {t('DashLabel_SelectAnNkrMemberCenter', 'Select an NKR Member Center')}
                                </h1>


                                <p>
                                    {t('DashLabel_NkrMemberCenterIntroLineOne', 'All the transplant centers listed below are NKR Member Centers. NKR Member Centers follow strict guidelines to ensure the highest level of care for living donors. All NKR Member Centers participate in:')}
                                </p>

                                <ul>
                                    <li>
                                        {t('DashLabel_NkrMemberCenterVoucherProgram', 'The Voucher Program, which allows donors to donate on their schedule and generate a voucher that can be redeemed by someone in need of a transplant')}
                                    </li>
                                    <li>
                                        {t('DashLabel_NkrMemberCenterDonorShield', 'Donor Shield, which offers comprehensive financial assistance, support, and protections for living donors, including lost wage and expense reimbursement')}
                                    </li>
                                </ul>

                                <p>
                                    <em>
                                        {t('DashLabel_NkrMemberCenterIntroLineTwo', 'Only centers listed below can be used for NKR kidney donation. If a facility is not listed, donation cannot take place there through NKR.')}
                                    </em>
                                </p>
                            </div>
                        </div>

                        <div ref={centerFilterRef} className="filter-bar">
                            <div className='filters'>
                                <div className='distance-filter'>
                                    <div style={{ margin: '0px 20px', fontSize: 16 }}>
                                        {t('DashLabel_ShowCentersWithin', 'Show Centers Within')}:
                                    </div>
                                    <div className="form-group" style={{ margin: 0 }}>
                                        {DISTANCE_OPTIONS.map((option) => (
                                            <div className="radio-inline" key={option.value}>
                                                <label className="control-label" style={{ margin: 0, padding: 0 }}>
                                                    <input
                                                        type="radio"
                                                        name="distance"
                                                        value={option.value}
                                                        checked={pendingDistance === option.value}
                                                        onChange={() => setPendingDistance(option.value)}
                                                    />{' '}
                                                    {t(option.key, option.fallback)}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className='adv-reimbursement-filter'>
                                    <div className="checkbox">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={pendingShowAdvanceReimbursementOnly}
                                                onChange={(e) => setPendingShowdvanceReimbursementOnly(e.target.checked)} /> Show Advance Reimbursement Centers Only
                                        </label>
                                    </div>
                                </div>
                                <div className='filter-button'>
                                    <button
                                        type="button"
                                        className="btn btn-filter btn-sm btn-block"
                                        disabled={isRefreshing}
                                        onClick={() => onChangeFilter(pendingDistance, pendingShowAdvanceReimbursementOnly)}
                                    >
                                        {t('Word_Filter', 'Filter')}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div
                                className="col-xs-12"
                                style={
                                    isRefreshing ?
                                        { opacity: 0.5, pointerEvents: 'none', transition: 'opacity .15s' }
                                        :
                                        { transition: 'opacity .15s' }
                                }
                            >
                                {submitError && <div className="alert alert-danger">{submitError}</div>}

                                {model.universalDashOption && (
                                    <div className='card'>
                                        <div className='card-body'>
                                            <div className='card-title'>{t('DashLabel_ReferralCenter', 'Transplant Center You Initiated Registration Through')}</div>

                                            <CenterTable
                                                centers={[model.universalDashOption]}
                                                {...tableProps}
                                            />
                                            <p>
                                                <strong>
                                                    {t('DashLabel_ReferalCenterLineOne', 'If you are within comfortable traveling distance, we recommend that you choose this center to expedite the donation process.')}
                                                </strong>
                                            </p>
                                            <p>
                                                <strong>
                                                    {t('DashLabel_ReferalCenterLineTwo', 'However, you may choose any NKR Center in the referral network below to donate.')}
                                                </strong>
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {model.patientCenterOption && (
                                    <div className='card no-border'>
                                        <div className='card-body'>
                                            <div className='card-title'><h2 style={{ margin: 0 }}>{t('DashLabel_IntendedRecipientCenter', "Intended Recipient's Transplant Center")}</h2></div>
                                            {model.patientCenterOption && (
                                                <CenterTable
                                                    centers={[model.patientCenterOption]}
                                                    {...tableProps}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className='card no-border'>
                                    <div className='card-body'>
                                        <div className='card-title'><h2 style={{ margin: 0 }}>{t('DashLabel_DcnCenters', 'Donor Care Network Centers of Excellence')}</h2></div>
                                        <div className='card-text'>{t('DashLabel_DcnCentersDescription', 'An elite group of transplant centers offering the highest level of care and support to living kidney donors. Some DCN centers offer advanced reimbursement through Donor Shield, allowing donors to receive reimbursement for eligible expenses before costs are incurred rather than waiting until after the donation process.')}</div>
                                        <br />
                                        {model.dcnOptions.length > 0 && (
                                            <CenterTable
                                                centers={model.dcnOptions}
                                                {...tableProps}
                                            />
                                        )}
                                    </div>
                                </div>

                                {model.nutcrackerOptions.length > 0 && (
                                    <div className='card no-border'>
                                        <div className='card-body'>
                                            <div className='card-title'><h2 style={{ margin: 0 }}>{t('DashLabel_NutcrackerCenters', 'Nutcracker Centers of Excellence')}</h2></div>
                                            <CenterTable
                                                centers={model.nutcrackerOptions}
                                                {...tableProps}
                                            />
                                        </div>
                                    </div>
                                )}

                                {model.otherOptions.length > 0 && (
                                    <div className='card no-border'>
                                        <div className='card-body'>
                                            <div className='card-title'><h2 style={{ margin: 0 }}>{t('DashLabel_OtherCenters', 'Other NKR Member Centers')}</h2></div>
                                            <CenterTable
                                                centers={model.otherOptions}
                                                {...tableProps}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {!hasCenters && (
                                <div className="col-xs-12">
                                    <div className="alert alert-warning">
                                        {t('Alert_IfYouDonTSeeAnySelectableCentersModifyTheFilterDistance', "If you don't see any selectable centers, modify the filter distance at the top of the page")}
                                        .
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {selectedCenter && (
                <CenterSelectModals
                    center={selectedCenter}
                    t={t}
                    isSubmitting={isSubmitting}
                    onConfirm={() => void onConfirmSelect()}
                    onClose={() => setSelectedCenter(null)}
                />
            )}
        </>
    );
};

export default NkrCenters;
